// @ts-nocheck
// Exact live version restored from the production sourcemap (portal.rebels.io, 2026-10-23).
import { jsx as _jsx, jsxs as _jsxs } from 'react/jsx-runtime';
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Select, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Add, Trash, ArrowLeft2, ArrowRight2 } from 'iconsax-react';
import dayjs from 'dayjs';
import { groupMatrixRows, matrixDates } from './weekly-matrix';
import { createHourEntryId } from '@application/features/HoursRegistration/hour-write-safety';
export function WeekHoursMatrix({ rows, types, errors, context, disabled, onChange }) {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('md'));
    const allDates = matrixDates(rows[0]?.date);
    const [activeDay, setActiveDay] = useState(() => Math.max(0, allDates.indexOf(rows[0]?.date.slice(0, 10))));
    const firstDate = rows[0]?.date;
    useEffect(() => {
        if (firstDate)
            setActiveDay(Math.max(0, matrixDates(firstDate).indexOf(firstDate.slice(0, 10))));
    }, [firstDate]);
    const dates = mobile ? allDates.slice(activeDay, activeDay + 1) : allDates;
    const groups = useMemo(() => groupMatrixRows(rows), [rows]);
    const allTypes = Array.from(new Map(Object.values(types)
        .flat()
        .map((type) => [type.id, type])).values());
    const validDate = (date) => Object.keys(types).find((key) => key.slice(0, 10) === date);
    const isTechnical = (row) => row.storedSystemCode === 'NO_EXPORT' && !allTypes.some((type) => type.id === row.assignmentReasonId);
    const amount = (row) => isTechnical(row) || allTypes.find((type) => type.id === row.assignmentReasonId)?.exportPolicy === 'NeverExport'
        ? 0
        : Number(row.hours) || 0;
    const [newTypeId, setNewTypeId] = useState('');
    const addType = () => {
        if (!newTypeId)
            return;
        const additions = Object.entries(types)
            .filter(([, choices]) => choices.some((type) => type.id === newTypeId))
            .map(([date]) => {
            const id = createHourEntryId();
            return { localId: `matrix-${id}`, persistedId: id, date, assignmentReasonId: newTypeId, hours: '', isPersisted: false };
        });
        onChange([...rows, ...additions]);
        setNewTypeId('');
    };
    return _jsxs(Box, {
        sx: { minWidth: 0 },
        children: [
            mobile &&
                _jsxs(Box, {
                    sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 },
                    children: [
                        _jsx(IconButton, {
                            'aria-label': 'Previous day',
                            disabled: activeDay === 0,
                            onClick: () => setActiveDay((day) => day - 1),
                            children: _jsx(ArrowLeft2, { size: 18 })
                        }),
                        _jsx(Typography, { fontWeight: 600, children: dayjs(dates[0]).format('dddd D MMM') }),
                        _jsx(IconButton, {
                            'aria-label': 'Next day',
                            disabled: activeDay === 6,
                            onClick: () => setActiveDay((day) => day + 1),
                            children: _jsx(ArrowRight2, { size: 18 })
                        })
                    ]
                }),
            _jsx(Box, {
                sx: { overflowX: 'auto' },
                children: _jsxs(Box, {
                    component: 'table',
                    'aria-label': 'Weekly hours matrix',
                    sx: {
                        width: '100%',
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed',
                        minWidth: mobile ? 0 : 740,
                        '& th, & td': {
                            px: { xs: 1, lg: 1.5 },
                            py: 0.75,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            verticalAlign: 'top'
                        },
                        '& th': { color: 'text.secondary', fontWeight: 500, fontSize: 12 },
                        '& td': { fontVariantNumeric: 'tabular-nums' }
                    },
                    children: [
                        _jsx('thead', {
                            children: _jsxs('tr', {
                                children: [
                                    _jsx('th', {
                                        style: { width: mobile ? '57%' : '25%', textAlign: 'left' },
                                        children: 'Hour type / destination'
                                    }),
                                    dates.map((date) => _jsxs('th', {
                                        children: [
                                            _jsx(Box, { children: dayjs(date).format('ddd') }),
                                            _jsx(Typography, {
                                                color: 'text.primary',
                                                fontSize: 14,
                                                fontWeight: 600,
                                                children: dayjs(date).format('D')
                                            })
                                        ]
                                    }, date)),
                                    !mobile && _jsx('th', { style: { width: 68, textAlign: 'right' }, children: 'Total' })
                                ]
                            })
                        }),
                        _jsx('tbody', {
                            children: groups.map((group) => {
                                const first = group.rows[0];
                                const type = allTypes.find((item) => item.id === group.typeId);
                                const choices = allTypes.filter((item) => group.rows.every((row) => (types[row.date] ?? []).some((choice) => choice.id === item.id)));
                                const selectedAvailable = choices.some((item) => item.id === group.typeId);
                                const technical = group.rows.some(isTechnical);
                                const label = type?.displayName || first.storedDisplayName || 'Hour type';
                                const route = type?.routingTarget === 'ClientAssignment'
                                    ? context.displayName
                                    : type?.routingTarget === 'InternalDepartment'
                                        ? `${context.internalHoursDepartmentName || context.internalDepartmentCode || 'Linked Internal Department'} · automatic`
                                        : technical
                                            ? 'System entry · not exported'
                                            : 'Previously saved entry';
                                const canRemove = group.rows.every((row) => rows.filter((other) => other.date === row.date).length > 1);
                                return _jsxs('tr', {
                                    children: [
                                        _jsxs('td', {
                                            children: [
                                                _jsxs(Select, {
                                                    size: 'small',
                                                    fullWidth: true,
                                                    value: group.typeId,
                                                    disabled: disabled || technical || choices.length === 0,
                                                    inputProps: {
                                                        'aria-label': `Hour type ${label}${group.occurrence ? ` entry ${group.occurrence + 1}` : ''}`
                                                    },
                                                    sx: { borderRadius: 2, fontSize: 14, '& .MuiSelect-select': { py: 0.75 } },
                                                    onChange: (event) => onChange(rows.map((row) => group.rows.includes(row)
                                                        ? { ...row, assignmentReasonId: event.target.value }
                                                        : row)),
                                                    children: [
                                                        !selectedAvailable &&
                                                            _jsxs(MenuItem, {
                                                                value: group.typeId,
                                                                children: [label, ' \u00B7 unavailable']
                                                            }),
                                                        choices.map((item) => _jsx(MenuItem, { value: item.id, children: item.displayName }, item.id))
                                                    ]
                                                }),
                                                _jsxs(Box, {
                                                    sx: { display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 },
                                                    children: [
                                                        _jsx(Typography, {
                                                            variant: 'caption',
                                                            color: 'text.secondary',
                                                            sx: { flex: 1, lineHeight: 1.4 },
                                                            children: route
                                                        }),
                                                        group.rows.some((row) => errors[row.localId]?.assignmentReasonId) &&
                                                            _jsx(Typography, {
                                                                variant: 'caption',
                                                                color: 'error.main',
                                                                role: 'alert',
                                                                children: 'Choose an hour type available for every date in this line.'
                                                            }),
                                                        groups.length > 1 &&
                                                            !technical &&
                                                            canRemove &&
                                                            _jsx(IconButton, {
                                                                size: 'small',
                                                                'aria-label': `Remove ${label} line ${group.occurrence + 1}`,
                                                                disabled: disabled,
                                                                onClick: () => onChange(rows.filter((row) => !group.rows.includes(row))),
                                                                children: _jsx(Trash, { size: 15 })
                                                            })
                                                    ]
                                                })
                                            ]
                                        }),
                                        dates.map((date) => {
                                            const row = group.rows.find((item) => item.date.slice(0, 10) === date);
                                            const wireDate = validDate(date);
                                            const canAdd = group.occurrence === 0 &&
                                                wireDate &&
                                                (types[wireDate] ?? []).some((item) => item.id === group.typeId);
                                            const unavailable = !row && !canAdd;
                                            return _jsx('td', {
                                                style: { background: dayjs(date).day() % 6 === 0 ? '#FAFBFC' : undefined },
                                                children: unavailable
                                                    ? _jsx(Box, {
                                                        sx: { py: 1, textAlign: 'center', color: 'text.secondary' },
                                                        'aria-label': `${label} unavailable on ${date}`,
                                                        children: '\u2014'
                                                    })
                                                    : _jsx(TextField, {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        // Empty cells stay empty; empty is saved as 0.
                                                        value: row?.hours ?? '',
                                                        disabled: disabled || Boolean(row && isTechnical(row)),
                                                        error: Boolean(row && errors[row.localId]?.hours),
                                                        helperText: row && errors[row.localId]?.hours,
                                                        inputProps: {
                                                            'aria-label': `${label}, ${date}${group.occurrence ? `, entry ${group.occurrence + 1}` : ''}`,
                                                            inputMode: 'numeric',
                                                            style: { textAlign: 'center', padding: '8px 2px' }
                                                        },
                                                        sx: {
                                                            '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                                            '& .MuiFormHelperText-root': { mx: 0, fontSize: 11 }
                                                        },
                                                        onChange: (event) => {
                                                            if (row && row.fromEmptyCell && event.target.value.trim() === '')
                                                                // Clearing a cell that was empty before restores the original state.
                                                                onChange(rows.filter((item) => item !== row));
                                                            else if (row)
                                                                onChange(rows.map((item) => item === row
                                                                    ? { ...item, hours: event.target.value }
                                                                    : item));
                                                            else {
                                                                const id = createHourEntryId();
                                                                onChange([
                                                                    ...rows,
                                                                    {
                                                                        localId: `matrix-${id}`,
                                                                        persistedId: id,
                                                                        date: wireDate,
                                                                        assignmentReasonId: group.typeId,
                                                                        hours: event.target.value,
                                                                        isPersisted: false,
                                                                        fromEmptyCell: true
                                                                    }
                                                                ]);
                                                            }
                                                        }
                                                    })
                                            }, date);
                                        }),
                                        !mobile &&
                                            _jsxs('td', {
                                                style: { textAlign: 'right', fontWeight: 600 },
                                                children: [group.rows.reduce((sum, row) => sum + amount(row), 0), ' h']
                                            })
                                    ]
                                }, group.key);
                            })
                        }),
                        _jsx('tfoot', {
                            children: _jsxs('tr', {
                                children: [
                                    _jsx('td', { style: { fontWeight: 600 }, children: 'Total per day' }),
                                    dates.map((date) => _jsx('td', {
                                        style: { textAlign: 'center', fontWeight: 600 },
                                        children: rows
                                            .filter((row) => row.date.slice(0, 10) === date)
                                            .reduce((sum, row) => sum + amount(row), 0)
                                    }, date)),
                                    !mobile &&
                                        _jsxs('td', {
                                            style: { textAlign: 'right', fontWeight: 600 },
                                            children: [rows.reduce((sum, row) => sum + amount(row), 0), ' h']
                                        })
                                ]
                            })
                        })
                    ]
                })
            }),
            _jsxs(Box, {
                sx: { p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' },
                children: [
                    _jsxs(Select, {
                        size: 'small',
                        displayEmpty: true,
                        value: newTypeId,
                        disabled: disabled,
                        onChange: (event) => setNewTypeId(event.target.value),
                        inputProps: { 'aria-label': 'New hour type' },
                        sx: { minWidth: 170, borderRadius: 2, fontSize: 14 },
                        children: [
                            _jsx(MenuItem, { value: '', disabled: true, children: 'Select hour type' }),
                            allTypes.map((type) => _jsx(MenuItem, { value: type.id, children: type.displayName }, type.id))
                        ]
                    }),
                    _jsx(Button, {
                        variant: 'outlined',
                        startIcon: _jsx(Add, { size: 18 }),
                        disabled: disabled || !newTypeId,
                        onClick: addType,
                        children: 'Add hour type'
                    })
                ]
            })
        ]
    });
}
